/**
 * useIsAdmin Hook
 *
 * Checks if the current user has admin privileges.
 * Supports both legacy admin role and new platform role hierarchy.
 *
 * Admin roles:
 * - user_metadata.role === 'admin' (legacy)
 * - user_metadata.platform_role === 'admin-platform' (platform admin - can manage everything)
 * - user_metadata.platform_role === 'admin-tenant' (tenant admin - can manage agency/departments)
 *
 * NOTE: This hook now uses centralized auth state from AppShellContext.
 * It no longer has its own Supabase auth listener, preventing race conditions
 * when multiple listeners react to token refreshes.
 */

import { useAdminStatus } from '@/components/shell/AppShellContext';
import type { AdminStatus, AdminRole } from '@/lib/routes/types';

// Re-export types for backwards compatibility
export type { AdminRole };

/**
 * Hook to check if the current user has admin privileges
 *
 * Uses centralized auth state from AppShellContext instead of its own listener.
 * This prevents race conditions when multiple listeners react to token refreshes.
 *
 * @returns AdminStatus object with admin role information
 */
export function useIsAdmin(): AdminStatus {
  // Use centralized admin status from AppShellContext
  return useAdminStatus();
}

/**
 * Simple version that just returns boolean
 */
export function useIsAdminSimple(): boolean {
  const { isAdmin } = useIsAdmin();
  return isAdmin;
}

export default useIsAdmin;
